import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect, useCallback } from 'react';
import HeaderControls from './HeaderControls.js';
import PDFContentProvider, { usePDFContent } from './PDF.js';
import TinyPagesList from './TinyPagesList.js';
import { useResourceDocumentDataContextSafe } from '../../context/resourceDataContext.js';
import { useExpandedFdocContext } from '../ExpandedFdocProvider.js';
import { PdfDocument } from './PDFDocument.js';
import { styled } from 'styled-components';
import { useQueryUserInfo } from '../../../../user/queries/useQueryUserInfo.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { usePDFViewControls, PDFViewControlsContextProvider } from './PDFViewControls.js';
import { useWheel } from '../../../../../node_modules/@use-gesture/react/dist/use-gesture-react.esm.js';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import { useThrottledCallback } from '@fabric-app/hooks/useThrottledCallback';
import { useDisableNativeGestures } from '../../../../hooks/useDisableNativeGestures.js';
import { useDoubleTap } from '../../../../hooks/useDoubleTap.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Trackpad } from '../../../../utils/detectTrackpad.js';
import { Button } from '@fabric-app/ui/components/Button';
import { IconDesktop16MiscSearch } from '@fabric-app/icons/svg';
import { formatPercent } from '@fabric-app/utils/formatting';
import ZoomSlider from '@fabric-app/ui/components/ZoomSlider/ZoomSlider';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { useQueryComments } from '../../../../comments/queries/useQueryComments.js';
import PDFToolbar from './PDFToolbar.js';

const SizerWrapper = styled.div.attrs((p) => {
  const { fullEstimatedHeight } = usePDFContent();
  const { zoomLevel } = usePDFViewControls();
  return {
    ...p,
    style: {
      ...p.style,
      height: fullEstimatedHeight * zoomLevel
    }
  };
}).withConfig({
  displayName: "StoredPDFContent__SizerWrapper",
  componentId: "sc-d7a34194-0"
})([
  `position:relative;`
]);
const ContentPDF = styled.div.attrs((p) => {
  const { fullEstimatedHeight } = usePDFContent();
  return {
    ...p,
    style: {
      ...p.style,
      height: fullEstimatedHeight
    }
  };
}).withConfig({
  displayName: "StoredPDFContent__ContentPDF",
  componentId: "sc-d7a34194-1"
})([
  `display:flex;position:relative;height:100%;overflow:hidden;&[pan-mode='true']{cursor:grab;*{cursor:grab;}&[data-panning='true']{cursor:grabbing;user-select:none;*{cursor:grabbing;user-select:none;}}&[data-zooming='true']{cursor:zoom-in;*{cursor:zoom-in;}}}&::-webkit-scrollbar{width:0.5rem;height:0.5rem;}&::-webkit-scrollbar-thumb{background:rgb(146 146 146 / 30%);border-radius:1.5rem;visibility:hidden;}&:hover::-webkit-scrollbar-thumb{visibility:visible;}&::-webkit-scrollbar-thumb:active,&::-webkit-scrollbar-thumb:hover{background-color:rgb(146 146 146 / 40%);visibility:visible;}&::-webkit-scrollbar-track{background:rgb(0 0 0 / 7%);border-radius:1.5rem;visibility:hidden;}&:hover::-webkit-scrollbar-track{visibility:visible;}flex:1;`
]);
const PDFInner = styled.div.withConfig({
  displayName: "StoredPDFContent__PDFInner",
  componentId: "sc-d7a34194-2"
})([
  `display:flex;flex-grow:1;max-height:100%;`
]);
const DocumentLoader = styled.div.withConfig({
  displayName: "StoredPDFContent__DocumentLoader",
  componentId: "sc-d7a34194-3"
})([
  `position:absolute;inset:0;background:white;z-index:10;display:flex;justify-content:center;align-items:center;`
]);
const ErrorDisplay = styled.div.withConfig({
  displayName: "StoredPDFContent__ErrorDisplay",
  componentId: "sc-d7a34194-4"
})([
  `display:flex;flex-direction:column;gap:16px;justify-content:center;align-items:center;height:100%;width:100%;font-size:14px;font-weight:600;letter-spacing:-0.025em;text-align:center;background:white;> button{text-decoration:underline;}:last-child{margin-bottom:10px;}`
]);
const TINY_PAGE_WIDTH_FULL_SCREEN = 79;
const defaultLoadState = {
  error: null,
  retryAttempts: 0,
  loading: true
};
const StoredPDFContent = ({ fileUrl: fileUrlProp, tinyPagesIsVisible, isFullScreen }) => {
  const { resource, fullName } = useResourceDocumentDataContextSafe();
  const { comments } = useQueryComments(resource.id);
  const [currentPage, setCurrentPage] = useState(1);
  const { pdf, setPdf } = useExpandedFdocContext();
  const contentRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const { isMobileView, isTabletView } = useResponsive();
  const { data: user } = useQueryUserInfo();
  const [loadState, setLoadState] = useState(defaultLoadState);
  const fileUrl = fileUrlProp || resource.fileUrl;
  const onTryAgain = (e) => {
    e?.preventDefault();
    setLoadState(defaultLoadState);
  };
  const onLoadError = (error) => {
    setLoadState((state) => {
      const nextRetryAttempts = state.retryAttempts + 1;
      if (nextRetryAttempts > 3) {
        return {
          error,
          loading: false,
          retryAttempts: nextRetryAttempts
        };
      }
      return {
        ...state,
        error,
        retryAttempts: nextRetryAttempts
      };
    });
  };
  const [hasSelection, setHasSelection] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  useEffect(() => {
    if (!contentRef.current) return;
    const onSelectionChange = (_) => {
      if (!contentRef.current) return;
      const selection = window.getSelection();
      if (!selection) {
        setHasSelection(false);
        return;
      }
      if (!contentRef.current.contains(selection.anchorNode)) {
        setHasSelection(false);
        return;
      }
      if (selection.type !== "Range") {
        setHasSelection(false);
        return;
      }
      setHasSelection(true);
    };
    document.addEventListener("selectionchange", onSelectionChange, true);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange, true);
    };
  }, [
    contentRef
  ]);
  useEffect(() => {
    if (!contentRef.current) return;
    let mouseDown = false;
    let lastElement = null;
    const onMouseDown = () => {
      setIsMouseDown(true);
      mouseDown = true;
    };
    const onMouseUp = () => {
      setIsMouseDown(false);
      mouseDown = false;
      if (lastElement) lastElement.dataset["current"] = "false";
    };
    const onMouseMove = (e) => {
      if (!mouseDown) return;
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (!element) return;
      if (lastElement) lastElement.dataset["current"] = "false";
      lastElement = element;
      lastElement.dataset["current"] = "true";
    };
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("mouseup", onMouseUp, true);
    window.addEventListener("mousemove", onMouseMove, true);
    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("mouseup", onMouseUp, true);
      window.removeEventListener("mousemove", onMouseMove, true);
    };
  }, [
    contentRef
  ]);
  const mobileZoomUiVisible = useBoolState();
  const motionScale = useMotionValue(1);
  const motionLeft = useTransform(motionScale, [
    1,
    0.2
  ], [
    "0%",
    "40%"
  ]);
  const setAnimateZoomLevel = useThrottledCallback(({ value: nextZoomLevel, prevValue: prevZoomLevel, disableAnimation }) => {
    if (disableAnimation) {
      motionScale.set(nextZoomLevel);
      return;
    }
    const dur = Math.min(Math.abs(nextZoomLevel - prevZoomLevel) * 1.5, 0.2);
    let currentScrollLeft = scrollAreaRef.current?.scrollLeft || 0;
    let currentScrollTop = scrollAreaRef.current?.scrollTop || 0;
    animate(motionScale, nextZoomLevel, {
      duration: dur,
      ease: "linear",
      visualDuration: dur,
      onUpdate: (value) => {
        if (scrollAreaRef.current) {
          const prevZoomLevel2 = motionScale.getPrevious() || 1;
          const nextZoomLevel2 = value;
          const diff = nextZoomLevel2 / prevZoomLevel2;
          const containerWidth = scrollAreaRef.current.clientWidth;
          const containerScrollTop = currentScrollTop || scrollAreaRef.current.scrollTop;
          const containerScrollLeft = currentScrollLeft || scrollAreaRef.current.scrollLeft;
          const currentCenterY = scrollAreaRef.current.clientHeight / 2 + containerScrollTop;
          const futureCenterY = currentCenterY * diff;
          const futureScrollY = futureCenterY - scrollAreaRef.current.clientHeight / 2;
          const currentCenterX = containerWidth / 2 + containerScrollLeft;
          const futureCenterX = currentCenterX * diff;
          const futureScrollX = futureCenterX - containerWidth / 2;
          const scrollTo = {
            top: Math.round(futureScrollY),
            left: Math.round(futureScrollX)
          };
          currentScrollLeft = futureScrollX;
          currentScrollTop = futureScrollY;
          scrollAreaRef.current.scrollTo(scrollTo);
        }
      }
    });
  }, 70);
  const pdfUiControls = usePDFViewControls({
    onZoomLevelChange(value) {
      setAnimateZoomLevel.current(value);
    }
  });
  const setZoomLevel = pdfUiControls.setZoomLevel;
  const fitFirstPageToContainer = useCallback(async (pdf2, rect, disableAnimation) => {
    if (rect && pdf2) {
      const page = await pdf2.getPage(1);
      const viewport = page.getViewport({
        scale: 1
      });
      const actualWidth = viewport.width;
      const actualHeight = viewport.height;
      const baseWidthScale = rect.width / actualWidth;
      const scaleBaseHeight = actualHeight * baseWidthScale;
      const scaleBaseWidth = actualWidth * baseWidthScale;
      const futureScaleWidth = Math.min(rect.width, 1e3) / scaleBaseWidth;
      const futureScaleHeight = rect.height / scaleBaseHeight;
      const minScale = Math.min(futureScaleWidth, futureScaleHeight, 1);
      setZoomLevel(minScale, {
        disableAnimation: disableAnimation ?? true
      });
    }
  }, [
    setZoomLevel
  ]);
  const onRectChange = useCallback((rect) => {
    if (rect && pdf && scrollAreaRectRef.current?.width) {
      const resizeDiff = scrollAreaRectRef.current?.width - rect.width;
      if (Math.abs(resizeDiff) > 100) {
        fitFirstPageToContainer(pdf, rect);
      }
    }
  }, [
    fitFirstPageToContainer,
    pdf
  ]);
  const [scrollAreaRect] = useDomContentRect(scrollAreaRef.current, {
    observeAttributes: false,
    onRectChange
  });
  const scrollAreaRectRef = useRef(scrollAreaRect);
  scrollAreaRectRef.current = scrollAreaRect;
  const incrementZoomLevel = (value) => {
    pdfUiControls.setZoomLevel(value, {
      increment: true
    });
  };
  useWheel((state) => {
    if (!state.event.ctrlKey && !state.event.metaKey || state.event.type !== "wheel") {
      return false;
    }
    state.event.preventDefault();
    state.event.stopPropagation();
    Trackpad.detect(state.event);
    const change = state.type === "pointermove" || Trackpad.isTrackpad() ? state.delta[1] * -0.015 : 0.125 * state.direction[1] * -1;
    incrementZoomLevel(change);
  }, {
    target: scrollAreaRef.current || void 0,
    eventOptions: {
      passive: false
    }
  });
  const resetZoomLevel = useCallback(() => {
    if (pdfUiControls.zoomLevel === 1) return;
    pdfUiControls.setZoomLevel(1);
  }, [
    pdfUiControls
  ]);
  const { onClick: onDoubleTapResetZoomLevel } = useDoubleTap((e) => {
    const relevantTarget = e.target instanceof Element ? e.target : e.target instanceof Node ? e.target.parentElement : null;
    if (!isMobileView && relevantTarget?.closest(".textLayer")) {
      return;
    }
    if (pdfUiControls.zoomLevel === 1) {
      pdfUiControls.setZoomLevel(2);
    } else {
      resetZoomLevel();
    }
  }, 150, {
    onSingleTap: mobileZoomUiVisible.handleFalse
  });
  useDisableNativeGestures();
  const onLoadSuccess = useCallback(async (nextPDF) => {
    setLoadState({
      ...defaultLoadState,
      loading: false
    });
    setPdf(nextPDF);
    if (scrollAreaRectRef.current) {
      fitFirstPageToContainer(nextPDF, scrollAreaRectRef.current);
    }
  }, [
    fitFirstPageToContainer,
    scrollAreaRectRef,
    setPdf
  ]);
  const isFullScreenDebounced = useDebounce(isFullScreen, 200);
  const isFullScreenSwitch = isFullScreenDebounced !== isFullScreen;
  return loadState.error ? /* @__PURE__ */ jsxs(ErrorDisplay, {
    children: [
      /* @__PURE__ */ jsx("h6", {
        children: "PDF failed to load."
      }),
      /* @__PURE__ */ jsx("button", {
        onClick: onTryAgain,
        children: "Click to retry"
      })
    ]
  }) : /* @__PURE__ */ jsxs(PDFContentProvider, {
    pdf,
    tinyPagesVisible: tinyPagesIsVisible,
    currentPage,
    setCurrentPage,
    contentRef,
    comments,
    resourceId: resource.id,
    width: scrollAreaRect?.width || 0,
    children: [
      pdf && fileUrl && /* @__PURE__ */ jsx(HeaderControls, {
        pdf,
        isOwner: resource.user?.id === user?.id,
        fileName: fullName,
        storedFileUrl: fileUrl
      }),
      /* @__PURE__ */ jsxs(PdfContainer, {
        overflow: "hidden",
        flexGrow: 1,
        "data-pan-id-ignore-resource-drag-resize": "xy",
        style: {
          opacity: isFullScreenSwitch ? 0 : 1,
          transition: "opacity 0.05s ease-in-out"
        },
        children: [
          (isMobileView || isTabletView) && /* @__PURE__ */ jsxs(MobileZoomControlContainer, {
            children: [
              /* @__PURE__ */ jsx(AnimatePresence, {
                children: mobileZoomUiVisible.value && /* @__PURE__ */ jsx(MobileZoomDragContainer, {
                  ...framerAnimationSlideFromRight,
                  children: /* @__PURE__ */ jsx(ZoomSlider, {
                    hideButtonControls: true,
                    steps: zoomSequence,
                    value: pdfUiControls.zoomLevel,
                    onChange: pdfUiControls.setZoomLevel,
                    orientation: "vertical"
                  })
                })
              }),
              /* @__PURE__ */ jsxs(Button, {
                variant: "bg-secondary",
                onClick: mobileZoomUiVisible.handleToggle,
                style: {
                  fontVariantNumeric: "tabular-nums"
                },
                children: [
                  /* @__PURE__ */ jsx(IconDesktop16MiscSearch, {}),
                  " ",
                  formatPercent(pdfUiControls.zoomLevel)
                ]
              })
            ]
          }),
          tinyPagesIsVisible && !isMobileView && /* @__PURE__ */ jsx(TinyPagesContainer, {
            children: /* @__PURE__ */ jsx(ScrollArea.Default, {
              scrollbarVariant: "subtle",
              style: {
                height: "100%"
              },
              children: /* @__PURE__ */ jsx(TinyPagesList, {
                pageWidth: TINY_PAGE_WIDTH_FULL_SCREEN
              })
            })
          }),
          /* @__PURE__ */ jsx(StyledScrollArea, {
            asFlex: true,
            innerDisplayFlex: true,
            orientation: "both",
            scrollbarVariant: "subtle",
            viewportRef: scrollAreaRef,
            onClick: onDoubleTapResetZoomLevel,
            style: {
              position: "relative",
              width: "100%"
            },
            children: /* @__PURE__ */ jsx(SizerWrapper, {
              children: /* @__PURE__ */ jsxs(ContentPDF, {
                as: motion.div,
                style: {
                  transformOrigin: "top left",
                  position: "relative",
                  scale: motionScale,
                  left: motionLeft,
                  willChange: "scale transform",
                  ["--zoom-level"]: motionScale
                },
                "data-pan-id-ignore-resource-drag-resize": "xy",
                "data-resource-id": resource.id,
                "data-scaled-down": pdfUiControls.zoomLevel < 1,
                ref: contentRef,
                children: [
                  /* @__PURE__ */ jsxs(PDFInner, {
                    children: [
                      loadState.loading && /* @__PURE__ */ jsx(DocumentLoader, {
                        children: /* @__PURE__ */ jsx(Spinner, {
                          size: 30,
                          thickness: 3
                        })
                      }),
                      /* @__PURE__ */ jsx(PdfDocument, {
                        fileUrl,
                        onLoadSuccess,
                        onLoadError,
                        hasSelection,
                        isMouseDown,
                        tinyPagesIsVisible,
                        width: scrollAreaRect?.width || 0
                      }, `${fileUrl}--${loadState.retryAttempts}`)
                    ]
                  }),
                  /* @__PURE__ */ jsx(PDFToolbar, {})
                ]
              })
            })
          })
        ]
      })
    ]
  });
};
const StoredPDFContentWithProvider = (props) => {
  return /* @__PURE__ */ jsx(PDFViewControlsContextProvider, {
    children: /* @__PURE__ */ jsx(StoredPDFContent, {
      ...props
    })
  });
};
const zoomSequence = [
  0.25,
  0.3,
  0.35,
  0.4,
  0.45,
  0.5,
  0.55,
  0.6,
  0.65,
  0.7,
  0.75,
  0.8,
  0.85,
  0.9,
  0.95,
  1,
  1.05,
  1.1,
  1.15,
  1.2,
  1.25,
  1.3,
  1.35,
  1.4,
  1.45,
  1.5,
  1.55,
  1.6,
  1.65,
  1.7,
  1.75,
  1.8,
  1.85,
  1.9,
  1.95,
  2,
  2.05,
  2.1,
  2.15,
  2.2,
  2.25,
  2.3,
  2.35,
  2.4,
  2.45,
  2.5,
  2.55,
  2.6,
  2.65,
  2.7,
  2.75,
  2.8,
  2.85,
  2.9,
  2.95,
  3
];
const PdfContainer = styled(Flex).withConfig({
  displayName: "StoredPDFContent__PdfContainer",
  componentId: "sc-d7a34194-5"
})([
  `position:relative;`
]);
const StyledScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "StoredPDFContent__StyledScrollArea",
  componentId: "sc-d7a34194-6"
})([
  `position:relative;& > [data-radix-scroll-area-viewport]{overflow-anchor:none;}`
]);
const TinyPagesContainer = styled.div.withConfig({
  displayName: "StoredPDFContent__TinyPagesContainer",
  componentId: "sc-d7a34194-7"
})([
  `width:108px;flex-shrink:0;border-right:1px solid `,
  `;height:100%;`
], cssVar["color-border-primary"]);
const MobileZoomControlContainer = styled.div.withConfig({
  displayName: "StoredPDFContent__MobileZoomControlContainer",
  componentId: "sc-d7a34194-8"
})([
  `position:absolute;bottom:13px;right:13px;z-index:10;`
]);
const MobileZoomDragContainer = styled(motion.div).withConfig({
  displayName: "StoredPDFContent__MobileZoomDragContainer",
  componentId: "sc-d7a34194-9"
})([
  `background:`,
  `;border-radius:8px;transform-origin:bottom right;right:0;position:absolute;height:50px;width:170px;padding:0 15px;& [data-zoom-slider-container]{height:100%;}`
], cssVar["color-bg-secondary-button"]);
const framerAnimationSlideFromRight = {
  initial: {
    opacity: 0,
    y: "-62px",
    x: "-20px",
    rotate: "90deg"
  },
  exit: {
    y: "-62px",
    x: "-20px",
    rotate: "90deg",
    opacity: 0
  },
  animate: {
    opacity: 1,
    y: "-62px",
    x: "-50px",
    rotate: "90deg"
  },
  transition: {
    duration: 0.15,
    ease: "easeInOut"
  }
};

export { StoredPDFContentWithProvider as default };
//# sourceMappingURL=StoredPDFContent.js.map
